/**
 * The do-not-contact list.
 *
 * Every campaign screens against this before it screens against anything else,
 * which is why it has its own page rather than living inside one campaign: an
 * address that asked us to stop asked all of us to stop, not one campaign.
 *
 * Rows are the CRM's pills, like every other list in this app. The source is a
 * status pill rather than a grey word, because it is the column that decides
 * what may be done to the row: a row the recipient created is permanent, and
 * the design has one vocabulary for "this record's standing" already.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, AtSign, Ban, ChevronLeft, ChevronRight, Globe, Loader2, Plus, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { listSuppression, removeSuppression, type SuppressionRow } from "../../../lib/marketing";
import {
  Btn, EmptyState, MenuField, PageHead, SearchField, StatusPill, Toolbar, ToolbarGap, fmtNum, fmtStamp,
  type StatusTone, listCard,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { Note } from "./shared";
import { SOURCE_TONE, SUPPRESSION_SOURCES } from "./suppression/sources";
import { AddSuppressionModal } from "./suppression/AddSuppressionModal";
import { EraseAddressModal } from "./suppression/EraseAddressModal";
import { SuppressionRowMenu } from "./suppression/SuppressionRowMenu";

/** Sources the recipient chose. These rows are permanent by design. */
const RECIPIENT_DECIDED = new Set(["unsubscribe", "complaint"]);

const ALL = "__all__";

/** Kind, source, note, added - four tracks plus the actions column, shared by
 *  the head and the rows so a new column cannot shift one without the other. */
const COLS = "minmax(0,2.2fr) 1.1fr minmax(0,1.7fr) 1fr 44px";

/** The design's page size. The endpoint pages server-side, so this is the real
 *  window on the list and not a slice of a bigger one already in memory. */
const PAGE_SIZE = 50;

export function SuppressionPanel({ user }: { user: StaffUser }) {
  const t = useT();
  const canManage = userCanPlatform(user, "manage:crm.marketing");

  const [rows, setRows] = useState<SuppressionRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [page, setPage] = useState(1);

  const [q, setQ] = useState("");
  const [committedQ, setCommittedQ] = useState("");
  const [kind, setKind] = useState<string>(ALL);
  const [source, setSource] = useState<string>(ALL);

  const [showAdd, setShowAdd] = useState(false);
  const [showErase, setShowErase] = useState(false);
  const [confirmDel, setConfirmDel] = useState<SuppressionRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setCommittedQ(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  // Any change to what is being asked for starts at the first page. Staying on
  // page 4 of a result set that now has one page shows an empty card and reads
  // as "the filter found nothing".
  useEffect(() => {
    setPage(1);
  }, [committedQ, kind, source]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await listSuppression({
        q: committedQ || undefined,
        kind: kind === ALL ? undefined : (kind as "email" | "domain"),
        source: source === ALL ? undefined : source,
        page,
        pageSize: PAGE_SIZE,
      });
      setRows(res.rows);
      setTotal(res.total);
      setPageCount(Math.max(1, res.pageCount));
    } catch {
      setRows([]);
      setError(t("marketing.error.load"));
    }
  }, [committedQ, kind, source, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(row: SuppressionRow) {
    try {
      await removeSuppression(row.id);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== row.id));
      setTotal((n) => Math.max(0, n - 1));
      setError(null);
    } catch {
      setError(t("marketing.suppression.removeRefused"));
    } finally {
      setConfirmDel(null);
    }
  }

  const filtered = committedQ !== "" || kind !== ALL || source !== ALL;

  return (
    // One open menu at a time across every row.
    <PopHost>
      <PageHead
        title={t("marketing.suppression.title")}
        sub={t("marketing.suppression.intro")}
        actions={
          <>
            <Link to="/marketing/campaigns" className="crm-btn">
              <ChevronLeft className="size-4" />
              {t("marketing.back")}
            </Link>
            {/* Erasure lives here rather than on its own page because this is
                where its result lands: everything else about the person goes,
                and the row it leaves on this list is the whole point. It is a
                plain button, not the brand one - adding an address is the
                everyday action, erasing somebody is not. */}
            {canManage ? (
              <Btn onClick={() => setShowErase(true)}>
                <Trash2 className="size-4" />
                {t("marketing.erase.title")}
              </Btn>
            ) : null}
            {canManage ? (
              <Btn tone="brand" onClick={() => setShowAdd(true)}>
                <Plus className="size-4" />
                {t("marketing.suppression.add")}
              </Btn>
            ) : null}
          </>
        }
      />

      {error ? (
        <Note tone="bad" icon={<AlertTriangle className="size-3.5" />} className="mb-3.5">
          {error}
        </Note>
      ) : null}

      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder={t("marketing.suppression.searchPlaceholder")} />
        <MenuField
          value={kind}
          onChange={setKind}
          label={t("marketing.suppression.col.value")}
          options={[
            { value: ALL, label: t("marketing.suppression.kindAny") },
            { value: "email", label: t("marketing.suppression.kind.email"), icon: <AtSign className="size-4" /> },
            { value: "domain", label: t("marketing.suppression.kind.domain"), icon: <Globe className="size-4" /> },
          ]}
        />
        <MenuField
          value={source}
          onChange={setSource}
          label={t("marketing.suppression.col.source")}
          options={[
            { value: ALL, label: t("marketing.suppression.sourceAny") },
            ...SUPPRESSION_SOURCES.map((s) => ({ value: s as string, label: t(`marketing.suppression.source.${s}`) })),
          ]}
        />
        <ToolbarGap />
        <span className="crm-sub px-1.5">
          {rows ? t("marketing.suppression.count", { count: fmtNum(total) }) : t("common.loading")}
        </span>
      </Toolbar>

      <div className="crm-card" style={listCard(COLS)}>
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          // An empty page and an empty filter are different facts, and the one
          // that says "nobody has ever unsubscribed" when three filters are on
          // is the one that sends somebody looking for a bug in the sender.
          <EmptyState
            icon={Ban}
            title={t(filtered ? "marketing.suppression.noMatch" : "marketing.suppression.empty")}
            body={t(filtered ? "marketing.suppression.noMatchBody" : "marketing.suppression.emptyBody")}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("marketing.suppression.col.value")}</span>
              <span>{t("marketing.suppression.col.source")}</span>
              <span>{t("marketing.suppression.col.note")}</span>
              <span>{t("marketing.suppression.col.added")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((r) => {
                const permanent = RECIPIENT_DECIDED.has(r.source);
                const Kind = r.kind === "domain" ? Globe : AtSign;
                return (
                  <div key={r.id} className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="crm-av crm-av-soft">
                        <Kind className="size-4" />
                      </span>
                      <span className="min-w-0">
                        {/* A domain row is written with the @ it behaves like:
                            it suppresses every address at that domain, not a
                            mailbox called "company.ee". */}
                        <span className="crm-prow-n crm-tnum crm-trunc block">
                          {r.kind === "domain" ? `@${r.value}` : r.value}
                        </span>
                        <span className="crm-prow-s crm-trunc block">
                          {t(`marketing.suppression.kind.${r.kind}`)}
                          {permanent ? ` · ${t("marketing.suppression.permanent")}` : ""}
                        </span>
                      </span>
                    </span>

                    <span>
                      <StatusPill tone={SOURCE_TONE[r.source] ?? "mut"}>
                        {t(`marketing.suppression.source.${r.source}`)}
                      </StatusPill>
                    </span>

                    <span className="crm-mut crm-trunc text-[12.5px]">{r.note ?? r.reason ?? "-"}</span>
                    <span className="crm-mut crm-tnum text-[12.5px]">{fmtStamp(r.createdAt)}</span>

                    <SuppressionRowMenu
                      row={r}
                      canRemove={canManage && !permanent}
                      onRemove={() => setConfirmDel(r)}
                    />
                  </div>
                );
              })}
            </div>

            <div className="crm-tfoot">
              {t("marketing.suppression.showing", {
                from: (page - 1) * PAGE_SIZE + 1,
                to: (page - 1) * PAGE_SIZE + rows.length,
                total,
              })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>

      {showAdd ? (
        <AddSuppressionModal
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            setError(null);
            void load();
          }}
          onError={() => {
            setShowAdd(false);
            setError(t("marketing.suppression.addError"));
          }}
        />
      ) : null}

      {showErase ? (
        <EraseAddressModal
          onClose={() => setShowErase(false)}
          onDone={() => {
            setError(null);
            void load();
          }}
        />
      ) : null}

      {confirmDel ? (
        <ConfirmModal
          title={t("marketing.suppression.removeTitle")}
          body={t("marketing.suppression.confirmRemove", { value: confirmDel.value })}
          confirmLabel={t("marketing.suppression.remove")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => remove(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </PopHost>
  );
}
